/**
 * reducers/order-review.ts
 * desc: reducers for document review
 * note:
 *
 */
import { VIEWS } from '../../modules/OrderReviewsDetail/constants';
import { getItem } from '../../utilities/local-storage';
import { IS_LIST_VIEW_ACTIVE_KEY, SELECTED_ORDER_SCHEMA_ID_KEY } from '../actions/order-review';
import type { ActiveField, OrderFileWithUrl, OrderMetadataAutocomplete, OrderReviewAction } from '../actions/order-review';
import type { OrdersAction } from '../actions/orders';
import constants from '../../settings/constants';
import type { Reducer } from '@reduxjs/toolkit';
import type { OrderDto } from '../../types/api/orders';
import type { OrderSchemaMetadataDto } from '../../types/api/order-schemas';
import type { OrderReviewItemDto } from '../../types/api/review-queues';

// the review item under edit; an order opened on its own (GET_ORDER_SUCCESS) carries only `order`
export type OrderReviewItem = Partial<OrderReviewItemDto>;

export interface OrderReviewState {
    angle: number;
    activeField: ActiveField;
    activeTab: string;
    currentPage: number;
    currentView: string;
    isLoading: {
        updateOrderSchemaFields: boolean;
        getOrderMetadataAutocomplete: boolean;
        getOrderReviewItemNext: boolean;
        getOrderSchemasMetadata: boolean;
        updateOrderReviewItem: boolean;
        // absent until the orders slice's GET_ORDER_* actions pass through — INITIAL_STATE never sets it
        getOrder?: boolean;
    };
    isSettingsActive: boolean;
    isSettingPreviewActive: boolean;
    isListViewActive: boolean;
    // a schema id, or an env-pill expression for the session (see setSelectedOrderSchemaId)
    selectedOrderSchemaId: string;
    orderMetadataAutocomplete: OrderMetadataAutocomplete;
    orderReviewItem: OrderReviewItem | undefined;
    orderSchemasMetadata: OrderSchemaMetadataDto[];
    orderEditVersion: number;
    silentSaveEditVersion: number;
    savedOrderEditVersion: number;
    isSilentSaveFailed: boolean;
    silentSaveError: string | null;
}

// the orders slice's GET_ORDER_* actions land here too: an order opened on its own becomes the review item
type HandledAction = OrderReviewAction | Extract<OrdersAction, { type: 'GET_ORDER_ERROR' | 'GET_ORDER_REQUEST' | 'GET_ORDER_SUCCESS' }>;

const INITIAL_STATE: OrderReviewState = {
    angle: 0,
    activeField: {
        name: null,
        index: null,
    },
    activeTab: 'none',
    currentPage: 1,
    currentView: VIEWS.DOCUMENT,
    isLoading: {
        updateOrderSchemaFields: false,
        getOrderMetadataAutocomplete: false,
        getOrderReviewItemNext: false,
        getOrderSchemasMetadata: false,
        updateOrderReviewItem: false,
    },
    isSettingsActive: false,
    isSettingPreviewActive: false,
    isListViewActive: (getItem(IS_LIST_VIEW_ACTIVE_KEY) as boolean | null | undefined) ?? false,
    selectedOrderSchemaId: (getItem(SELECTED_ORDER_SCHEMA_ID_KEY) as string | null | undefined) || constants.DEFAULT_ORDER_SCHEMA_ID,
    orderMetadataAutocomplete: { requestKey: null, query: null, items: [] },
    orderReviewItem: undefined,
    orderSchemasMetadata: [],
    // monotonic counter of local order edits vs. its value captured when a silent save
    // fired: equal on response = nothing changed locally while the save was in flight,
    // so the response can be adopted wholesale (-1 = no silent save captured yet)
    orderEditVersion: 0,
    silentSaveEditVersion: -1,
    // the edit-version last acknowledged by a successful save, plus whether a background
    // autosave has failed since — together they drive the unsaved-changes indicator
    savedOrderEditVersion: 0,
    isSilentSaveFailed: false,
    silentSaveError: null,
};


function documentReviewReducer(state: OrderReviewState = INITIAL_STATE, action: HandledAction): OrderReviewState {
    switch (action.type) {
        // CLEAR Order Review
        case 'CLEAR_ORDER_REVIEW':
            return INITIAL_STATE;

        // SET Active Order Tab
        case 'SET_ACTIVE_ORDER_TAB':
            return _setActiveOrderPage(state, action.payload);

        // SET Page
        case 'SET_PAGE':
            return _setPage(state, action.payload);

        // SET Order View
        case 'SET_ORDER_VIEW':
            return _setOrderView(state, action.payload);

        // SET Angle
        case 'SET_ANGLE':
            return _setAngle(state, action.payload);

        // SET Active Filed
        case 'SET_ACTIVE_FIELD':
            return _setActiveField(state, action.payload);

        // UPDATE Order Info
        case 'SET_ORDER_INFO':
            return _updateOrderInfo(state, action.payload);

        // SET Is Settings Active
        case 'SET_IS_SETTINGS_ACTIVE':
            return { ...state, isSettingsActive: action.payload };

        // SET Is Setting Preview Active
        case 'SET_IS_SETTING_PREVIEW_ACTIVE':
            return { ...state, isSettingPreviewActive: action.payload };

        // SET Is List View Active
        case 'SET_IS_LIST_VIEW_ACTIVE':
            return { ...state, isListViewActive: action.payload };

        // GET Order Metadata Autocomplete
        case 'GET_ORDER_METADATA_AUTOCOMPLETE_ERROR':
            return _getOrderMetadataAutocompleteError(state, action.payload);
        case 'GET_ORDER_METADATA_AUTOCOMPLETE_REQUEST':
            return _getOrderMetadataAutocompleteRequest(state);
        case 'GET_ORDER_METADATA_AUTOCOMPLETE_SUCCESS':
            return _getOrderMetadataAutocompleteSuccess(state, action.payload);

        // GET Order
        case 'GET_ORDER_ERROR':
            return _getOrderError(state);
        case 'GET_ORDER_REQUEST':
            return _getOrderRequest(state);
        case 'GET_ORDER_SUCCESS':
            return _getOrderSuccess(state, action.payload);

        // Selected Order Schema (shared across all OrderSchemaPicker instances; the payload may
        // be a real id or an env-pill expression — see INITIAL_STATE)
        case 'SET_SELECTED_ORDER_SCHEMA_ID':
            return { ...state, selectedOrderSchemaId: action.payload };

        // GET Order Schemas Metadata
        case 'GET_ORDER_SCHEMAS_METADATA_ERROR':
            return _getOrderSchemasMetadataError(state);
        case 'GET_ORDER_SCHEMAS_METADATA_REQUEST':
            return _getOrderSchemasMetadataRequest(state);
        case 'GET_ORDER_SCHEMAS_METADATA_SUCCESS':
            return _getOrderSchemasMetadataSuccess(state, action.payload);

        // UPDATE Order Schema Fields
        case 'UPDATE_ORDER_SCHEMA_FIELDS_ERROR':
            return _updateOrderSchemaFieldsError(state);
        case 'UPDATE_ORDER_SCHEMA_FIELDS_REQUEST':
            return _updateOrderSchemaFieldsRequest(state, action.payload);
        case 'UPDATE_ORDER_SCHEMA_FIELDS_SUCCESS':
            return _updateOrderSchemaFieldsSuccess(state, action.payload);

        // GET Order Review Item Next
        case 'GET_ORDER_REVIEW_ITEM_NEXT_ERROR':
        case 'GET_ORDER_REVIEW_ITEM_ASSIGN_ERROR':
            return _getOrderReviewItemNextError(state);
        case 'GET_ORDER_REVIEW_ITEM_NEXT_REQUEST':
        case 'GET_ORDER_REVIEW_ITEM_ASSIGN_REQUEST':
            return _getOrderReviewItemNextRequest(state);
        case 'GET_ORDER_REVIEW_ITEM_NEXT_SUCCESS':
        case 'GET_ORDER_REVIEW_ITEM_ASSIGN_SUCCESS':
            return _getOrderReviewItemNextSuccess(state, action.payload);

        // UPDATE Order Review Item
        case 'UPDATE_ORDER_REVIEW_ITEM_ERROR':
            return _updateOrderReviewItemError(state, action.isSilent, action.errorMessage);
        case 'UPDATE_ORDER_REVIEW_ITEM_REQUEST':
            return _updateOrderReviewItemRequest(state, action.isSilent);
        case 'UPDATE_ORDER_REVIEW_ITEM_SUCCESS':
            return _updateOrderReviewItemSuccess(state, action.payload, { isSilent: action.isSilent });

        default:
            return state;
    }
}



//*************************************************
//*****         SET Active Order Tab     **********
//*************************************************
function _setActiveOrderPage(state: OrderReviewState, tab: string): OrderReviewState {
    return {
        ...state,
        activeTab: tab,
    };
}



//*************************************************
//*****         SET Page        *******************
//*************************************************
function _setPage(state: OrderReviewState, page: number): OrderReviewState {
    return {
        ...state,
        currentPage: page,
    };
}



//*************************************************
//*****         SET Order View             ********
//*************************************************
function _setOrderView(state: OrderReviewState, payload: string): OrderReviewState {
    return {
        ...state,
        currentView: payload,
    };
}



//*************************************************
//*****         SET Angle        ******************
//*************************************************
function _setAngle(state: OrderReviewState, angle: number): OrderReviewState {
    return {
        ...state,
        angle: angle,
    };
}



//*************************************************
//*****         SET Active Field      *************
//*************************************************
function _setActiveField(state: OrderReviewState, field: ActiveField): OrderReviewState {
    return {
        ...state,
        activeField: field,
        orderMetadataAutocomplete: { requestKey: null, query: null, items: [] },
    };
}



//*************************************************
//*****         UPDATE Order Info     *************
//*************************************************
function _updateOrderInfo(state: OrderReviewState, newOrder: OrderDto): OrderReviewState {
    return {
        ...state,
        orderReviewItem: {
            ...state.orderReviewItem,
            order: newOrder,
        },
        orderEditVersion: (state.orderEditVersion ?? 0) + 1,
    };
}



//*************************************************
//*****   GET Order Metadata Autocomplete   *******
//*************************************************
// keeping the failed requester's key marks the slot as resolved-empty for that field, so
// its pending-aware loading state settles on "no results" instead of loading forever
function _getOrderMetadataAutocompleteError(state: OrderReviewState, payload: { requestKey: string | null }): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderMetadataAutocomplete: false,
        },
        orderMetadataAutocomplete: { requestKey: payload?.requestKey ?? null, query: null, items: [] },
    };
}

function _getOrderMetadataAutocompleteRequest(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderMetadataAutocomplete: true,
        },
    };
}

function _getOrderMetadataAutocompleteSuccess(state: OrderReviewState, payload: OrderMetadataAutocomplete): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderMetadataAutocomplete: false,
        },
        orderMetadataAutocomplete: payload,
    };
}



//*************************************************
//*****   GET Order Schemas Metadata        *******
//*************************************************
function _getOrderSchemasMetadataError(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderSchemasMetadata: false,
        },
        orderSchemasMetadata: [],
    };
}

function _getOrderSchemasMetadataRequest(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderSchemasMetadata: true,
        },
    };
}

function _getOrderSchemasMetadataSuccess(state: OrderReviewState, payload: OrderSchemaMetadataDto[]): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderSchemasMetadata: false,
        },
        orderSchemasMetadata: payload,
    };
}



//*************************************************
//*****   UPDATE Order Schema Fields     *******
//*************************************************
function _updateOrderSchemaFieldsError(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateOrderSchemaFields: false,
        }
    };
}

function _updateOrderSchemaFieldsRequest(state: OrderReviewState, payload: OrderSchemaMetadataDto[]): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateOrderSchemaFields: true,
        },
        orderSchemasMetadata: payload,
    };
}

function _updateOrderSchemaFieldsSuccess(state: OrderReviewState, payload: OrderSchemaMetadataDto[]): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateOrderSchemaFields: false,
        },
        orderSchemasMetadata: payload,
    };
}



//*************************************************
//*****   GET Order Item        *******************
//*************************************************
function _getOrderError(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrder: false,
        },
        orderReviewItem: undefined,
    };
}

function _getOrderRequest(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrder: true,
        },
    };
}

function _getOrderSuccess(state: OrderReviewState, payload: OrderDto): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrder: false,
        },
        orderReviewItem: {
            order: payload,
        },
        // a fresh order from the server — nothing local is unsaved
        savedOrderEditVersion: state.orderEditVersion ?? 0,
        isSilentSaveFailed: false,
        silentSaveError: null,
    };
}



//*************************************************
//*****   GET Order Review Item Next        *******
//*************************************************
function _getOrderReviewItemNextError(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderReviewItemNext: false,
        },
        orderReviewItem: undefined,
    };
}

function _getOrderReviewItemNextRequest(state: OrderReviewState): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderReviewItemNext: true,
        },
    };
}

function _getOrderReviewItemNextSuccess(state: OrderReviewState, payload: OrderReviewItemDto): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getOrderReviewItemNext: false,
        },
        orderReviewItem: payload,
        // a fresh order from the server — nothing local is unsaved
        savedOrderEditVersion: state.orderEditVersion ?? 0,
        isSilentSaveFailed: false,
        silentSaveError: null,
    };
}



//*************************************************
//*****   UPDATE Order Review Item        *********
//*************************************************
function _updateOrderReviewItemError(state: OrderReviewState, isSilent?: boolean, errorMessage?: string): OrderReviewState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateOrderReviewItem: false,
        },
        // a failed background autosave leaves local edits unpersisted — flagged for the
        // unsaved-changes indicator until a later save succeeds (explicit actions surface
        // their own error toast instead); the message backs the indicator's hover tooltip
        isSilentSaveFailed: isSilent ? true : state.isSilentSaveFailed,
        silentSaveError: isSilent ? (errorMessage ?? state.silentSaveError) : state.silentSaveError,
    };
}

function _updateOrderReviewItemRequest(state: OrderReviewState, isSilent?: boolean): OrderReviewState {
    if (isSilent) {
        // capture the edit counter — compared on response to detect edits that raced the save
        return { ...state, silentSaveEditVersion: state.orderEditVersion ?? 0 };
    }
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateOrderReviewItem: true,
        },
    };
}

// adopt a save response as the new review item; the response carries no usable file URLs,
// so the locally known orderFiles are kept on the adopted payload
function _adoptResponse(state: OrderReviewState, isLoading: OrderReviewState['isLoading'], payload: OrderReviewItemDto): OrderReviewState {
    // the files in the store carry the URL fetchOrderFiles attached
    const currentOrderFiles = (state.orderReviewItem?.order?.orderFiles || []) as OrderFileWithUrl[];
    payload.order.orderFiles = currentOrderFiles.map(file => ({
        ...file,
        fileUrl: file.fileUrl || '', // Ensure fileUrl is present
    }));
    return { ...state, isLoading, orderReviewItem: payload };
}

function _updateOrderReviewItemSuccess(state: OrderReviewState, payload: OrderReviewItemDto, { isSilent }: { isSilent?: boolean } = {}): OrderReviewState {
    const isLoading = {
        ...state.isLoading,
        updateOrderReviewItem: false,
    };
    // this response proves a save reached the server: a silent save persisted the state
    // captured at its request, an explicit action persisted the current state wholesale
    const saved = {
        savedOrderEditVersion: isSilent ? (state.silentSaveEditVersion ?? 0) : (state.orderEditVersion ?? 0),
        isSilentSaveFailed: false,
        silentSaveError: null,
    };

    if (!isSilent) {
        return { ..._adoptResponse(state, isLoading, payload), ...saved };
    }

    const current = state.orderReviewItem;
    if (!current?.order) {
        return { ...state, isLoading, ...saved };
    }

    // silent autosave, and no local edit happened while the request was in flight (the
    // edit counter still matches the value captured when the save fired — the usual case,
    // since autosaves fire at quiet commit points): the response is exactly local state
    // plus what the server computed on top (sibling population including clears arriving
    // as null, canonical values, recomputed isMatched) — adopt it wholesale. The coalesced
    // defaults (0 vs -1) send states that never captured a silent save — hand-built test
    // fixtures included — into the conservative branch below.
    if (payload?.order && (state.orderEditVersion ?? 0) === (state.silentSaveEditVersion ?? -1)) {
        return { ..._adoptResponse(state, isLoading, payload), ...saved };
    }

    // local edits raced the request — local values win unconditionally: merge ONLY the
    // metadata arrays back in (order-level + per-line-item, matched by id). Values are
    // never adopted from a raced response — it is a stale snapshot of the payload it
    // acknowledged, and adopting from it wipes fresher local state (the next quiet save
    // syncs everything instead). Line items added locally after the request was sent keep
    // their absent metadata; items present only in the response (deleted locally
    // meanwhile) are ignored. Out-of-order responses are last-wins — every subsequent
    // save refreshes again.
    const responseLineItemsById = new Map(
        (payload?.order?.lineItems || []).map((lineItem) => [ lineItem.id, lineItem ])
    );
    const order = {
        ...current.order,
        metadata: payload?.order?.metadata ?? current.order.metadata,
        lineItems: (current.order.lineItems || []).map((lineItem) => {
            const responseLineItem = responseLineItemsById.get(lineItem.id);
            return responseLineItem ? { ...lineItem, metadata: responseLineItem.metadata } : lineItem;
        }),
    };

    return {
        ...state,
        isLoading,
        ...saved,
        orderReviewItem: {
            ...current,
            order,
        },
    };
}



export default documentReviewReducer as Reducer<OrderReviewState>;
